/**
 * Estado global controlado: usuario, preferencias y ajustes públicos.
 * Los componentes se suscriben y nunca mutan el estado directamente.
 */
import { DEFAULT_LAYOUT, findTheme, type LanguageCode } from '@manitype/core';
import { setLocale } from '../lib/i18n';
import { readJson, writeJson } from '../lib/storage';
import { authApi, statsApi, usersApi } from '../services/api';
import type { Me, PublicSettings, UserSettings } from '../services/types';

export interface AppState {
  user: Me | null;
  prefs: UserSettings;
  publicSettings: PublicSettings | null;
}

type Listener = (state: AppState, previous: AppState) => void;

const PREFS_KEY = 'mt.prefs.v1';

function defaultPrefs(): UserSettings {
  const browserLang: LanguageCode = navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'es';
  const prefersLight = window.matchMedia?.('(prefers-color-scheme: light)').matches;
  return {
    uiLanguage: browserLang,
    exerciseLanguage: browserLang,
    keyboardLayout: DEFAULT_LAYOUT[browserLang],
    theme: prefersLight ? 'paper' : 'midnight',
    accentColor: null,
    fontSize: 28,
    caretStyle: 'line',
    stopOnError: false,
    showKeyboard: true,
    soundEnabled: false,
    testMode: 'words',
    testDuration: 30,
  };
}

let state: AppState = {
  user: null,
  prefs: { ...defaultPrefs(), ...readJson<Partial<UserSettings>>(PREFS_KEY, {}) },
  publicSettings: null,
};
const listeners = new Set<Listener>();

export function getState(): AppState {
  return state;
}

function setState(patch: Partial<AppState>): void {
  const previous = state;
  state = { ...state, ...patch };
  applyAppearance(state.prefs);
  for (const listener of listeners) listener(state, previous);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Negro o blanco, el que más contraste dé sobre un color #rrggbb (WCAG). */
export function readableOn(hex: string): string {
  const channel = (i: number) => {
    const v = Number.parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#111111' : '#ffffff';
}

/** Aplica tema, acento e idioma; refleja las preferencias en localStorage (lo lee theme-init.js). */
export function applyAppearance(prefs: UserSettings): void {
  const root = document.documentElement;
  const theme = findTheme(prefs.theme) ? prefs.theme : 'midnight';
  root.dataset.theme = theme;
  if (prefs.accentColor) {
    root.style.setProperty('--accent', prefs.accentColor);
    root.style.setProperty('--accent-contrast', readableOn(prefs.accentColor));
  } else {
    root.style.removeProperty('--accent');
    root.style.removeProperty('--accent-contrast');
  }
  root.style.setProperty('--typing-size', `${prefs.fontSize}px`);
  setLocale(prefs.uiLanguage);
  writeJson(PREFS_KEY, prefs);
}

export function setUser(user: Me | null): void {
  setState(user ? { user, prefs: { ...user.settings } } : { user: null });
}

export async function bootstrap(): Promise<void> {
  applyAppearance(state.prefs);
  const [me, settings] = await Promise.allSettled([authApi.me(), statsApi.publicSettings()]);
  setState({
    user: me.status === 'fulfilled' ? me.value.user : null,
    prefs: me.status === 'fulfilled' && me.value.user ? { ...me.value.user.settings } : state.prefs,
    publicSettings: settings.status === 'fulfilled' ? settings.value.settings : null,
  });
}

export async function refreshUser(): Promise<void> {
  const { user } = await authApi.me();
  setUser(user);
}

export async function logout(): Promise<void> {
  await authApi.logout().catch(() => undefined);
  setState({ user: null });
}

/**
 * Actualiza preferencias: con sesión se guardan en el servidor; como invitado,
 * en el navegador. Devuelve el usuario actualizado si lo hay.
 */
export async function updatePrefs(patch: Partial<UserSettings>): Promise<void> {
  if (state.user) {
    const { user } = await usersApi.updateSettings(patch);
    setUser(user);
  } else {
    setState({ prefs: { ...state.prefs, ...patch } });
  }
}

/** Temas disponibles para el usuario actual (invitados: solo los predeterminados). */
export function unlockedThemes(): string[] {
  return state.user?.unlockedThemes ?? ['midnight', 'paper'];
}
